/**
 * Sprint 9.4 — Helpers puros para el preview lateral del FormPaquete.
 *
 * Pure-function module: no toca window/api/dom. Recibe vistas crudas y
 * devuelve resúmenes calculados. Trivialmente testeable.
 *
 * Modelo de precios en paquete (Sprint 7.4):
 *   - Cada Producto tiene `precio` (a la carta) y `precioEnPaquete` (cuando
 *     entra como ítem de paquete). Si `precioEnPaquete` es null, se cae al
 *     `precio` como fallback (RN-S7.4-09).
 *   - El cliente puede sustituir un renglón por uno de sus sustitutos válidos.
 *   - Sprint 10 (pricing simétrico): Δ por sustitución = sus.precioEnPaq − base.precioEnPaq.
 *     Si el sustituto es más barato, el Δ es negativo (descuento). Ya no hay clamp en 0.
 */

interface ProductoLike {
  id: number;
  nombre: string;
  precio: number;
  precioEnPaquete?: number | null;
}

export interface RenglonPreviewInput {
  productoId: number | null;
  cantidad: number;
  sustitutosIds: number[];
}

export interface RenglonPreviewSalida {
  posicion: number;
  baseNombre: string;
  basePrecioEnPaquete: number;
  cantidad: number;
  sustitutos: Array<{
    productoId: number;
    nombre: string;
    precioEnPaquete: number;
    /** Δ que cobra cuando el cliente elige este sustituto (>=0, por unidad). */
    deltaPorUnidad: number;
  }>;
  /** Δ total del renglón si el cliente elige el sustituto más caro. */
  deltaMaximoRenglon: number;
}

export interface ResumenPreview {
  /** Renglones con sus deltas calculados. Filtra renglones sin productoId. */
  renglones: RenglonPreviewSalida[];
  /** Suma de `deltaMaximoRenglon`. 0 si no hay sustitutos en ningún renglón. */
  deltaMaximoTotal: number;
  /** `precioBase + deltaMaximoTotal`. El máximo que la dueña podría cobrar. */
  precioMaximo: number;
  /** Cantidad de renglones con al menos un sustituto válido. */
  renglonesConSustitutos: number;
}

/** Devuelve el precio del producto cuando entra en un paquete (fallback al precio normal). */
function precioEnPaq(p: ProductoLike): number {
  return p.precioEnPaquete != null ? p.precioEnPaquete : p.precio;
}

/**
 * Calcula el resumen del preview para un paquete en edición.
 *
 * Decisiones:
 *   - Renglones sin producto base se ignoran (todavía no son válidos).
 *   - Sustitutos cuyo productoId no exista en `productosPorId` se ignoran
 *     (defensivo: si el catálogo cambia mientras se edita).
 *   - Δ se calcula por unidad y se multiplica por la cantidad del renglón.
 *   - Sprint 10: si el sustituto es más barato, el Δ es negativo (descuento real).
 */
export function calcularPreviewPaquete(
  precioBase: number,
  renglones: RenglonPreviewInput[],
  productosPorId: Map<number, ProductoLike>,
): ResumenPreview {
  const out: RenglonPreviewSalida[] = [];
  let deltaMaximoTotal = 0;
  let renglonesConSustitutos = 0;

  renglones.forEach((r, idx) => {
    if (r.productoId == null) return;
    const base = productosPorId.get(r.productoId);
    if (!base) return;
    const basePrecio = precioEnPaq(base);

    const sustitutos = r.sustitutosIds
      .map((sid) => productosPorId.get(sid))
      .filter((s): s is ProductoLike => s !== undefined)
      .map((s) => {
        const sPrecio = precioEnPaq(s);
        const deltaPorUnidad = sPrecio - basePrecio;
        return {
          productoId: s.id,
          nombre: s.nombre,
          precioEnPaquete: sPrecio,
          deltaPorUnidad,
        };
      });

    let deltaMaxRenglon = 0;
    if (sustitutos.length > 0) {
      const maxPorUnidad = Math.max(...sustitutos.map((s) => s.deltaPorUnidad));
      deltaMaxRenglon = maxPorUnidad * r.cantidad;
      renglonesConSustitutos++;
    }

    deltaMaximoTotal += deltaMaxRenglon;

    out.push({
      posicion: idx + 1,
      baseNombre: base.nombre,
      basePrecioEnPaquete: basePrecio,
      cantidad: r.cantidad,
      sustitutos,
      deltaMaximoRenglon: deltaMaxRenglon,
    });
  });

  return {
    renglones: out,
    deltaMaximoTotal,
    precioMaximo: precioBase + deltaMaximoTotal,
    renglonesConSustitutos,
  };
}
